'use client'

import { CircleDot, Settings } from 'lucide-react'
import { useCallback, useEffect, useId, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useUserSettings } from '@/components/user-settings-context'
import { AUTO_SAVE_DEBOUNCE_MS, useAutoSave } from '@/hooks/use-auto-save'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import {
  COMPLEXITY_EXPLANATION_MAX_LENGTH,
  COMPLEXITY_MAX_LENGTH,
} from '@/lib/postmortem-limits'
import { type RouterInputs, trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { formatFullDate } from './postmortem-time-utils'
import { useProblem } from './problem-context'
import { useEditorSettings } from './settings-modal/editor-settings-modal-context'

function formatElapsedMs(ms: number): string {
  const totalSeconds = Math.round(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60
  return [
    String(hours).padStart(2, '0'),
    String(mins).padStart(2, '0'),
    String(secs).padStart(2, '0'),
  ].join(':')
}

const DIFFICULTY_OPTIONS = [
  {
    value: 'again',
    label: '🥵 Again',
    description:
      'Generated no solutions beyond the brute force and had no useful code.',
    active:
      'border-red-600 bg-red-100 text-red-700 ring-2 ring-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-500 dark:ring-red-700',
    idle: 'border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50/50 dark:border-red-800 dark:text-red-400 dark:hover:border-red-600 dark:hover:bg-red-950/50',
  },
  {
    value: 'struggled',
    label: '😰 Hard',
    description:
      'Approach worked, but was sub-optimal, took too long, and/or had too many failed attempts.',
    active:
      'border-orange-500 bg-orange-100 text-orange-700 ring-2 ring-orange-300 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-500 dark:ring-orange-700',
    idle: 'border-orange-200 text-orange-600 hover:border-orange-400 hover:bg-orange-50/50 dark:border-orange-800 dark:text-orange-400 dark:hover:border-orange-600 dark:hover:bg-orange-950/50',
  },
  {
    value: 'intermediate_effort',
    label: '😅 Good',
    description:
      'Got an acceptable approach working in a reasonable amount of time with few issues.',
    active:
      'border-green-600 bg-green-100 text-green-700 ring-2 ring-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-500 dark:ring-green-700',
    idle: 'border-green-200 text-green-600 hover:border-green-400 hover:bg-green-50/50 dark:border-green-800 dark:text-green-400 dark:hover:border-green-600 dark:hover:bg-green-950/50',
  },
  {
    value: 'effortless',
    label: '😊 Easy',
    description:
      'Optimal approach identified quickly and implemented with little to no effort.',
    active:
      'border-blue-500 bg-blue-100 text-blue-700 ring-2 ring-blue-300 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-500 dark:ring-blue-700',
    idle: 'border-blue-200 text-blue-600 hover:border-blue-400 hover:bg-blue-50/50 dark:border-blue-800 dark:text-blue-400 dark:hover:border-blue-600 dark:hover:bg-blue-950/50',
  },
] as const

type FlashcardDifficulty = (typeof DIFFICULTY_OPTIONS)[number]['value']

/** What the autosave sends — the router's own input type, not a hand copy. */
type PostmortemSavePayload = RouterInputs['postmortem']['save']

/**
 * The client's own complexity claim (LG-639, spec D19) — first-class fields,
 * always shown, rather than a row in the reorderable question bank. The coach's
 * review hangs a separate comment target on the time claim and on the space
 * claim, so it has to be able to depend on both existing; a bank entry the
 * client can disable cannot carry that. The free-text "Was your solution
 * optimal? What was the time/space?" question stays in the bank untouched.
 *
 * Each row is a big-O expression plus a note that defines its variables, kept
 * apart so a client can write `O(n log n)` with nothing else and the review
 * renders exactly that.
 */
type ComplexityField = keyof Pick<
  PostmortemSavePayload,
  | 'timeComplexity'
  | 'timeComplexityExplanation'
  | 'spaceComplexity'
  | 'spaceComplexityExplanation'
>

/**
 * Derived from the router's own input rather than hand-copied: the payload
 * spreads this object, and excess-property checking does not survive a spread,
 * so a hand-written twin would keep compiling after a rename while the claim
 * silently stopped reaching the database. `Pick` fails loudly instead. The
 * values are plain `string` because these are controlled inputs — `null`
 * belongs to the column, `''` to the box.
 */
type ComplexityClaim = Record<ComplexityField, string>

const EMPTY_COMPLEXITY: ComplexityClaim = {
  timeComplexity: '',
  timeComplexityExplanation: '',
  spaceComplexity: '',
  spaceComplexityExplanation: '',
}

/**
 * One row of the Complexity block. `valueKey`/`noteKey` are keys of
 * `ComplexityClaim` — which is itself derived from the router payload — so a
 * row cannot be wired to a field the save does not carry. It does not stop a
 * row naming the *other* row's field; the pairing below is checked by the
 * tests, not by the type.
 */
const COMPLEXITY_ROWS = [
  {
    label: 'Time',
    valueKey: 'timeComplexity',
    noteKey: 'timeComplexityExplanation',
    valuePlaceholder: 'O(n log n)',
    notePlaceholder: 'where n is the length of the array',
  },
  {
    label: 'Space',
    valueKey: 'spaceComplexity',
    noteKey: 'spaceComplexityExplanation',
    valuePlaceholder: 'O(n)',
    notePlaceholder: 'the recursion stack',
  },
] as const satisfies readonly {
  label: string
  valueKey: keyof ComplexityClaim
  noteKey: keyof ComplexityClaim
  valuePlaceholder: string
  notePlaceholder: string
}[]

interface PostmortemSectionProps {
  sessionId: string
  problemId: string
  sessionStartIso: string
  runCount: number
  submitCount: number
  totalElapsedMs?: number | null
}

/**
 * The session facts — pure props, no server data. Extracted so the loading and
 * error states can show them too: they are the part of the card that is always
 * knowable, and blanking them while the answers load makes the whole section
 * pop in at full height.
 */
function SessionDetails({
  sessionStartIso,
  runCount,
  submitCount,
  totalElapsedMs,
}: Pick<
  PostmortemSectionProps,
  'sessionStartIso' | 'runCount' | 'submitCount' | 'totalElapsedMs'
>) {
  return (
    <div className="rounded-md border p-3">
      <div className="mb-3 grid grid-cols-3 gap-3">
        <div>
          <label className="text-muted-foreground mb-0.5 block text-xs font-medium">
            Date
          </label>
          <span className="text-sm font-semibold">
            {formatFullDate(sessionStartIso)}
          </span>
        </div>
        <div>
          <label className="text-muted-foreground mb-0.5 block text-xs font-medium">
            Code Runs
          </label>
          <span className="text-sm font-semibold">{runCount}</span>
        </div>
        <div>
          <label className="text-muted-foreground mb-0.5 block text-xs font-medium">
            Code Submissions
          </label>
          <span className="text-sm font-semibold">{submitCount}</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-muted-foreground mb-1 block text-xs font-medium">
            Total Duration
          </label>
          <span className="block pt-1 font-mono text-sm font-semibold">
            {totalElapsedMs != null ? formatElapsedMs(totalElapsedMs) : '—'}
          </span>
        </div>
      </div>
    </div>
  )
}

/* ── Component ────────────────────────────────────────────────── */

export function PostmortemSection({
  sessionId,
  problemId,
  sessionStartIso,
  runCount,
  submitCount,
  totalElapsedMs,
}: PostmortemSectionProps) {
  const { settings } = useUserSettings()
  const problem = useProblem()
  // The attempts pane can have several sessions expanded at once, so the
  // complexity inputs' `htmlFor` targets have to be unique per mounted section
  // or a label click focuses another session's box.
  const fieldIdPrefix = useId()
  const showDifficulty = settings.postmortem?.showDifficultyRating ?? true
  const approachReportingFlag = useFeatureFlag(
    'enable-postmortem-approach-used',
  )
  const editorSettings = useEditorSettings()

  // This row is edited by this very form, so a cached copy of it is never safe
  // to hydrate from: `staleTime` is 30s app-wide, and the form is remounted
  // every time the client collapses and re-expands the attempt card.
  const {
    data: existingData,
    isFetchedAfterMount,
    isSuccess,
    isError,
  } = trpc.postmortem.getBySessionId.useQuery(
    { sessionId },
    {
      // `refetchOnMount: 'always'` states the requirement locally rather than
      // deriving it from `staleTime` plus whatever the provider's defaults
      // happen to be. A global `refetchOnMount: false` added later would
      // otherwise leave `isFetchedAfterMount` false forever and make this
      // whole section silently vanish from every re-expanded attempt card.
      staleTime: 0,
      refetchOnMount: 'always',
      // The response is consumed exactly once, at mount, by a latched effect.
      // Refetching on window focus would re-read this row for every expanded
      // attempt card and throw all of it away.
      refetchOnWindowFocus: false,
    },
  )

  // Fetch reflection questions
  const { data: questionsData } = trpc.postmortem.getQuestions.useQuery()

  // No onError here: the save goes through useAutoSave, which logs failures and
  // owns the retry-on-next-edit behaviour.
  //
  // Deliberately no cache write-back on success either. A write-back only
  // helps when the save resolves; the read-side guarantee above holds even
  // when it fails, never returns, or is dispatched by `flushOnUnmount` long
  // after this component is gone.
  const saveMutation = trpc.postmortem.save.useMutation()

  // Form state
  const [difficulty, setDifficulty] = useState<FlashcardDifficulty | null>(null)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [reportedApproachId, setReportedApproachId] = useState<string | null>(
    null,
  )
  const [reportedApproachOther, setReportedApproachOther] = useState('')
  const [complexity, setComplexity] =
    useState<ComplexityClaim>(EMPTY_COMPLEXITY)
  const [initialized, setInitialized] = useState(false)

  // Initialize form state once data loads.
  //
  // Gated on `isFetchedAfterMount`, not merely on the data being present: on a
  // remount react-query hands over the cached row synchronously, and latching
  // onto that would refill the boxes from a copy older than the last save.
  // Every later edit sends the whole claim, so a stale `''` here would clear a
  // value the coach may already have commented on.
  useEffect(() => {
    if (initialized) return
    // `isSuccess` as well as `isFetchedAfterMount`: the latter means "a fetch
    // settled", not "a fetch succeeded" — query-core flips it on
    // `errorUpdateCount` too, and a failed refetch keeps the stale `data`, so
    // the boolean alone would wave exactly the row this guard rejects through.
    if (!isSuccess || !isFetchedAfterMount) return
    if (existingData === undefined) return // still loading

    if (existingData.postmortem) {
      const pm = existingData.postmortem
      setDifficulty(pm.flashcardDifficulty ?? null)
      setAnswers((pm.answers as Record<string, string>) ?? {})
      setReportedApproachId(pm.reportedApproachId ?? null)
      setReportedApproachOther(pm.reportedApproachOther ?? '')
      // Every column is nullable and the inputs are controlled, so each falls
      // back to '' rather than reaching an input as null.
      setComplexity({
        timeComplexity: pm.timeComplexity ?? '',
        timeComplexityExplanation: pm.timeComplexityExplanation ?? '',
        spaceComplexity: pm.spaceComplexity ?? '',
        spaceComplexityExplanation: pm.spaceComplexityExplanation ?? '',
      })
    } else {
      // No existing postmortem — set defaults from session
      setDifficulty(null)
      setAnswers({})
      setReportedApproachId(null)
      setReportedApproachOther('')
      setComplexity(EMPTY_COMPLEXITY)
    }

    setInitialized(true)
  }, [existingData, initialized, isFetchedAfterMount, isSuccess])

  const buildPayload = useCallback(
    (overrides?: {
      difficulty?: FlashcardDifficulty | null
      answers?: Record<string, string>
      reportedApproachId?: string | null
      reportedApproachOther?: string
      complexity?: ComplexityClaim
    }) => {
      const finalDifficulty =
        overrides?.difficulty !== undefined ? overrides.difficulty : difficulty
      const finalAnswers = overrides?.answers ?? answers
      const finalReportedApproachId =
        overrides?.reportedApproachId !== undefined
          ? overrides.reportedApproachId
          : reportedApproachId
      const finalReportedApproachOther =
        overrides?.reportedApproachOther !== undefined
          ? overrides.reportedApproachOther
          : reportedApproachOther
      const finalComplexity = overrides?.complexity ?? complexity

      const payload: PostmortemSavePayload = {
        problemId,
        sessionId,
        flashcardDifficulty: showDifficulty ? finalDifficulty : null,
        reportedApproachId: finalReportedApproachId,
        answers: finalAnswers,
        // Sent on every save, cleared field included: the router treats an
        // absent key as "this caller does not know about the field" and leaves
        // the stored claim alone, so a cleared box has to arrive as an explicit
        // empty string to actually clear.
        ...finalComplexity,
      }
      if (finalReportedApproachId === null) {
        payload.reportedApproachOther = finalReportedApproachOther
      }

      return payload
    },
    [
      difficulty,
      answers,
      complexity,
      problemId,
      reportedApproachId,
      reportedApproachOther,
      sessionId,
      showDifficulty,
    ],
  )

  // Autosave with debounce. Every call passes its own payload, so the hook needs
  // no ambient value. mutateAsync (not mutate) so the hook can serialize
  // overlapping saves — the payload is the whole postmortem, and an older
  // request landing last would overwrite newer answers. flushOnUnmount:
  // collapsing the postmortem must not discard what the user just typed.
  const { onChange: scheduleSave } = useAutoSave<PostmortemSavePayload>({
    onSave: (payload) => saveMutation.mutateAsync(payload),
    debounceMs: AUTO_SAVE_DEBOUNCE_MS.fast,
    flushOnUnmount: true,
    logLabel: 'Postmortem',
  })

  const triggerSave = useCallback(
    (overrides?: Parameters<typeof buildPayload>[0]) => {
      scheduleSave(buildPayload(overrides))
    },
    [buildPayload, scheduleSave],
  )

  // Every expand now waits on a real round trip (`refetchOnMount: 'always'`),
  // where it used to resolve from cache, so rendering nothing until then would
  // blank the card and then snap it to full height. The heading and the
  // session facts are pure props — they need no server data and are shown
  // immediately; only the answers wait.
  //
  // A failed fetch says so rather than removing the section: silently absent
  // reads to the client as "I never wrote one", which is the same lie the
  // whole ticket is about.
  if (!initialized) {
    return (
      <div className="text-foreground border-t border-gray-200 p-4 dark:border-gray-700">
        <h4 className="mb-3 text-sm font-semibold">Postmortem</h4>
        <SessionDetails
          sessionStartIso={sessionStartIso}
          runCount={runCount}
          submitCount={submitCount}
          totalElapsedMs={totalElapsedMs}
        />
        {isError ? (
          <p className="text-muted-foreground mt-3 text-xs">
            Couldn't load your postmortem. Collapse and re-open this attempt to
            try again.
          </p>
        ) : (
          <p
            className="text-muted-foreground mt-3 text-xs"
            aria-busy="true"
            aria-live="polite"
          >
            Loading your postmortem…
          </p>
        )}
      </div>
    )
  }

  const activeQuestions =
    questionsData?.questions.filter((q) => q.isEnabled || answers[q.id]) ?? []
  const approachOptions =
    problem.data?.editorial?.approaches
      ?.filter((approach) => approach.id)
      .map((approach) => ({
        id: approach.id,
        name: approach.name?.trim() || 'Untitled approach',
      })) ?? []

  return (
    <div className="text-foreground border-t border-gray-200 p-4 dark:border-gray-700">
      {/* Header */}
      <h4 className="mb-3 text-sm font-semibold">Postmortem</h4>

      <div className="space-y-4">
        <SessionDetails
          sessionStartIso={sessionStartIso}
          runCount={runCount}
          submitCount={submitCount}
          totalElapsedMs={totalElapsedMs}
        />

        {/* Difficulty Rating */}
        {showDifficulty && (
          <div>
            <label className="mb-1 block text-sm font-medium">
              Difficulty Rating
            </label>
            <p className="text-muted-foreground mb-2 text-xs">
              How hard was this problem for you?
            </p>
            <div className="grid grid-cols-5 gap-2">
              {DIFFICULTY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    const newVal = difficulty === opt.value ? null : opt.value
                    setDifficulty(newVal)
                    triggerSave({ difficulty: newVal })
                  }}
                  className={cn(
                    'rounded-md border py-1.5 text-sm font-medium transition-all',
                    difficulty === opt.value ? opt.active : opt.idle,
                  )}
                >
                  {opt.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setDifficulty(null)
                  triggerSave({ difficulty: null })
                }}
                className={cn(
                  'rounded-md border py-1.5 text-sm font-medium transition-all',
                  difficulty === null
                    ? 'border-gray-500 bg-gray-100 text-gray-700 ring-2 ring-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-500 dark:ring-gray-600'
                    : 'border-gray-200 text-gray-500 hover:border-gray-400 hover:bg-gray-50/50 dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-gray-800/50',
                )}
              >
                Skip
              </button>
            </div>
            {difficulty && (
              <p className="text-muted-foreground mt-2 text-xs">
                {
                  DIFFICULTY_OPTIONS.find((opt) => opt.value === difficulty)
                    ?.description
                }
              </p>
            )}
          </div>
        )}

        {/* Complexity — always shown (spec D19). Not a reflection question:
            the coach's review comments on the time claim and the space claim
            separately, so both fields have to exist for every client rather
            than depend on a bank row they can reorder or switch off. */}
        <div>
          <span className="mb-1 block text-sm font-medium">Complexity</span>
          <p className="text-muted-foreground mb-2 text-xs">
            What did your solution cost? Leave a row blank if you'd rather not
            say.
          </p>
          <div className="space-y-2">
            {COMPLEXITY_ROWS.map((row) => (
              <div key={row.valueKey} className="grid grid-cols-3 gap-2">
                <div>
                  <label
                    className="text-muted-foreground mb-1 block text-xs font-medium"
                    htmlFor={`${fieldIdPrefix}-${row.valueKey}`}
                  >
                    {row.label}
                  </label>
                  <Input
                    id={`${fieldIdPrefix}-${row.valueKey}`}
                    value={complexity[row.valueKey]}
                    placeholder={row.valuePlaceholder}
                    // The router's cap, shared rather than repeated. Over it,
                    // zod rejects the whole payload and the autosave has
                    // nowhere to say so — every later edit to this post-mortem
                    // would be dropped with the form still looking normal.
                    maxLength={COMPLEXITY_MAX_LENGTH}
                    onChange={(e) => {
                      const next = {
                        ...complexity,
                        [row.valueKey]: e.target.value,
                      }
                      setComplexity(next)
                      triggerSave({ complexity: next })
                    }}
                    className="h-8 font-mono"
                  />
                </div>
                <div className="col-span-2">
                  <label
                    className="text-muted-foreground mb-1 block text-xs font-medium"
                    htmlFor={`${fieldIdPrefix}-${row.noteKey}`}
                  >
                    Note
                  </label>
                  <Input
                    id={`${fieldIdPrefix}-${row.noteKey}`}
                    // Both rows label this box "Note", so the visible label
                    // alone would give two controls one accessible name.
                    aria-label={`${row.label} note`}
                    value={complexity[row.noteKey]}
                    placeholder={row.notePlaceholder}
                    maxLength={COMPLEXITY_EXPLANATION_MAX_LENGTH}
                    onChange={(e) => {
                      const next = {
                        ...complexity,
                        [row.noteKey]: e.target.value,
                      }
                      setComplexity(next)
                      triggerSave({ complexity: next })
                    }}
                    className="h-8"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Approach Reporting — gated until recommendation engine is ready */}
        {approachReportingFlag.enabled && (
          <div>
            <label
              className="mb-1 block text-sm font-medium"
              htmlFor="approach-used"
            >
              Approach Used
            </label>
            <p className="text-muted-foreground mb-2 text-xs">
              Select the approach you used, or choose Other and describe it.
            </p>
            <select
              id="approach-used"
              value={
                reportedApproachId &&
                approachOptions.some(
                  (option) => option.id === reportedApproachId,
                )
                  ? reportedApproachId
                  : 'other'
              }
              onChange={(e) => {
                const nextValue = e.target.value
                if (nextValue === 'other') {
                  setReportedApproachId(null)
                  triggerSave({ reportedApproachId: null })
                  return
                }
                setReportedApproachId(nextValue)
                triggerSave({
                  reportedApproachId: nextValue,
                })
              }}
              className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {approachOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
              <option value="other">Other</option>
            </select>
            {reportedApproachId === null && (
              <div className="mt-2">
                <label
                  className="text-muted-foreground mb-1 block text-xs font-medium"
                  htmlFor="other-approach"
                >
                  Other Approach
                </label>
                <Textarea
                  id="other-approach"
                  value={reportedApproachOther}
                  onChange={(e) => {
                    setReportedApproachOther(e.target.value)
                    triggerSave({ reportedApproachOther: e.target.value })
                  }}
                  rows={2}
                />
              </div>
            )}
            <p className="text-muted-foreground mt-2 text-xs">
              Recommendation-engine grading requires both a self-rating and a
              selected approach.
            </p>
          </div>
        )}

        {/* Reflection Questions */}
        {activeQuestions.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CircleDot className="text-muted-foreground h-4 w-4" />
                <span className="text-sm font-medium">
                  Reflection Questions
                </span>
              </div>
              <button
                type="button"
                onClick={() => editorSettings.open('postmortem')}
                className="text-muted-foreground hover:text-foreground rounded-xs p-1 transition-opacity hover:opacity-100"
                title="Configure postmortem settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              {activeQuestions.map((q) => (
                <div key={q.id}>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {q.questionText}
                  </label>
                  <Textarea
                    value={answers[q.id] ?? ''}
                    onChange={(e) => {
                      const newAnswers = {
                        ...answers,
                        [q.id]: e.target.value,
                      }
                      setAnswers(newAnswers)
                      triggerSave({ answers: newAnswers })
                    }}
                    rows={3}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
